// src/components/admin/pages/Dashboard.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, Sparkles, Award, BookOpen, AlertTriangle, Activity, ExternalLink, RefreshCw } from 'lucide-react';
import { dashboardApi } from '../../../services/adminApi';

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [warnings, setWarnings] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);
  const [loading, setLoading] = useState({ summary: true, warnings: true, activity: true });
  const [error, setError] = useState({ summary: null, warnings: null, activity: null });

  const fetchDashboardData = async () => {
    setLoading({ summary: true, warnings: true, activity: true });
    setError({ summary: null, warnings: null, activity: null });

    // Fetch all dashboard data in parallel
    const promises = [
      dashboardApi.getSummary().then(data => ({ type: 'summary', data })).catch(err => ({ type: 'summary', error: err.message })),
      dashboardApi.getWarnings().then(data => ({ type: 'warnings', data })).catch(err => ({ type: 'warnings', error: err.message })),
      dashboardApi.getRecentActivity(10).then(data => ({ type: 'activity', data })).catch(err => ({ type: 'activity', error: err.message }))
    ];

    try {
      const results = await Promise.allSettled(promises);

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const { type, data, error: resultError } = result.value;

          if (resultError) {
            setError(prev => ({ ...prev, [type]: resultError }));
          } else {
            switch (type) {
              case 'summary':
                setSummary(data);
                break;
              case 'warnings':
                setWarnings(data);
                break;
              case 'activity':
                setRecentActivity(data);
                break;
            }
          }
        } else {
          // Handle promise rejection (shouldn't happen with our catch above)
          const type = ['summary', 'warnings', 'activity'][index];
          setError(prev => ({ ...prev, [type]: 'Network error' }));
        }
      });
    } catch (err) {
      console.error('Dashboard data fetch failed:', err);
      setError({ summary: err.message, warnings: err.message, activity: err.message });
    } finally {
      setLoading({ summary: false, warnings: false, activity: false });
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);

      if (diffDays > 7) {
        return date.toLocaleDateString();
      } else if (diffDays > 0) {
        return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      } else {
        return 'Just now';
      }
    } catch {
      return 'Unknown';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  const getModuleLink = (module) => {
    switch (module) {
      case 'top_picks_pool': return '/admin/top-picks';
      case 'new_releases': return '/admin/new-releases';
      case 'book_of_week': return '/admin/book-of-week';
      default: return '/admin';
    }
  };

  // Quick action cards for navigation
  const quickActions = [
    { label: 'Top Picks Pool', icon: Star, to: '/admin/top-picks', color: 'text-blue-600', bgColor: 'bg-blue-50' },
    { label: 'New Releases', icon: Sparkles, to: '/admin/new-releases', color: 'text-green-600', bgColor: 'bg-green-50' },
    { label: 'Book of the Week', icon: Award, to: '/admin/book-of-week', color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
    { label: 'Books Library', icon: BookOpen, to: '/admin/books-library', color: 'text-purple-600', bgColor: 'bg-purple-50' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Dashboard</h1>
          <p className="text-text-secondary">Overview of content management status</p>
        </div>
        <button
          onClick={fetchDashboardData}
          disabled={loading.summary || loading.warnings || loading.activity}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading.summary || loading.warnings || loading.activity ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {loading.summary ? (
          // Loading skeleton for summary
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-bg-surface rounded-lg border border-gray-200 p-6">
              <div className="animate-pulse">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                    <div className="h-8 bg-gray-200 rounded w-12"></div>
                  </div>
                  <div className="w-8 h-8 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          ))
        ) : error.summary ? (
          <div className="col-span-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            Error loading summary: {error.summary}
          </div>
        ) : summary ? (
          <>
            <div className="bg-bg-surface rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-text-secondary text-sm">Active Top Picks</p>
                  <p className="text-2xl font-bold text-text-primary mt-1">
                    {summary.summary?.topPicksPool?.total || 0}
                  </p>
                </div>
                <Star className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-bg-surface rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-text-secondary text-sm">Active New Releases</p>
                  <p className="text-2xl font-bold text-text-primary mt-1">
                    {summary.summary?.newReleases?.total || 0}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {summary.summary?.newReleases?.activeLists || 0} list{summary.summary?.newReleases?.activeLists === 1 ? '' : 's'}
                  </p>
                </div>
                <Sparkles className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-bg-surface rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-text-secondary text-sm">Active Book of Week</p>
                  <p className="text-2xl font-bold text-text-primary mt-1">
                    {summary.summary?.bookOfTheWeek?.total || 0}
                  </p>
                </div>
                <Award className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* Quick Actions */}
      <div className="bg-bg-surface rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.map(({ label, icon: Icon, to, color, bgColor }) => (
            <Link
              key={label}
              to={to}
              className={`${bgColor} ${color} p-4 rounded-lg text-center hover:opacity-80 transition-opacity`}
            >
              <Icon className="w-6 h-6 mx-auto mb-2" />
              <p className="font-medium text-sm">{label}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Warnings */}
      <div className="bg-bg-surface rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <h2 className="text-lg font-semibold text-text-primary">Warnings</h2>
              {warnings && (
                <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                  {warnings.warningCount}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="p-6">
          {loading.warnings ? (
            <div className="animate-pulse space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-gray-200 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-48"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error.warnings ? (
            <div className="text-red-600 text-sm">Error loading warnings: {error.warnings}</div>
          ) : warnings && warnings.warnings?.length > 0 ? (
            <div className="space-y-4">
              {warnings.warnings.map((warning, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className={`w-2 h-2 rounded-full mt-2 ${getSeverityColor(warning.severity)}`} />
                  <div className="flex-1">
                    <div className="font-medium text-text-primary">{warning.message}</div>
                    {warning.description && (
                      <div className="text-text-secondary text-sm mt-1">{warning.description}</div>
                    )}
                    {warning.module && (
                      <Link
                        to={getModuleLink(warning.module)}
                        className="inline-flex items-center space-x-1 text-primary-600 text-xs mt-2 hover:text-primary-700"
                      >
                        <span>Go to {warning.module.replace('_', ' ')}</span>
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-text-secondary">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p>No warnings at this time</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-bg-surface rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-text-primary">Recent Activity</h2>
            {recentActivity && (
              <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                {recentActivity.count}
              </span>
            )}
          </div>
        </div>
        <div className="p-6">
          {loading.activity ? (
            <div className="animate-pulse space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-gray-200 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-40 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error.activity ? (
            <div className="text-red-600 text-sm">Error loading recent activity: {error.activity}</div>
          ) : recentActivity && recentActivity.activities?.length > 0 ? (
            <div className="space-y-4">
              {recentActivity.activities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mt-2" />
                  <div className="flex-1">
                    <div className="text-text-primary">{activity.description}</div>
                    <div className="text-text-secondary text-sm">
                      {formatTimestamp(activity.timestamp)}
                      {activity.module && (
                        <Link
                          to={getModuleLink(activity.module)}
                          className="ml-2 text-primary-600 hover:text-primary-700 inline-flex items-center space-x-1"
                        >
                          <span>→</span>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-text-secondary">
              <Activity className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p>No recent activity</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;